import axios from 'axios'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import { Science } from '@nine-thirty-five/material-symbols-react/sharp'
import { Alert, AlertTitle, Button, Card, CardActions, CardContent, CardMedia, Chip, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from '@iziplay/ui-kit'

const LASTFM_APIKEY = '945a859a9ebd7632ae4431538eaeb48a'

const ARTWORK_SIZE = 360

/**
 * ExternalAlbumModel from services/music -- one album shape for every
 * provider, so this page renders results without knowing who answered.
 *
 * The searches that still run in the browser (last.fm, and Spotify until its
 * API closed) map themselves onto it too, which is what lets them move behind
 * /external/search later without touching anything below.
 */
type ExternalAlbumLibrary = {
  artistId: string
  releaseId: string
  name: string
  complete: boolean
  completedTracks: number
  trackCount: number
}

type ExternalAlbum = {
  provider: string
  id: string
  name: string
  artist: string
  library?: ExternalAlbumLibrary
  artwork?: string
  year?: number
  trackCount?: number
  url?: string
}

type SearchType = 'last.fm' | 'spotify' | 'apple'

/*
 * Searched through the music service, in the shape above. Adding a provider
 * there is a string here and a radio button below.
 */
const SERVICE_PROVIDERS: SearchType[] = ['apple']

export function MusicSearch() {
  const navigate = useNavigate()
  const [results, setResults] = useState<ExternalAlbum[]>([])
  const [searchType, setSearchType] = useState<SearchType>('apple')

  const search = debounce(async (value: string) => {
    if (SERVICE_PROVIDERS.includes(searchType)) {
      const res = await axios.request<{ albums: ExternalAlbum[] }>({
        method: 'GET',
        url: `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/external/search`,
        params: {
          provider: searchType,
          q: value,
          artworkSize: ARTWORK_SIZE
        }
      })
      setResults(res.data.albums)
      return
    }

    switch (searchType) {
      case 'last.fm': {
        const res = await axios.request({
          method: 'GET',
          url: 'https://ws.audioscrobbler.com/2.0/',
          params: {
            method: 'album.search',
            api_key: LASTFM_APIKEY,
            format: 'json',
            album: value
          }
        })
        /*
         * No `library` on these: only the music service can say what we
         * already have, which is a reason to move this search behind
         * /external/search.
         */
        setResults(res.data.results.albummatches.album.map((a: any): ExternalAlbum => ({
          provider: 'last.fm',
          id: a.mbid,
          artist: a.artist,
          name: a.name,
          artwork: a.image.find((i: any) => i.size === 'large')['#text']
        })))
        break
      }
      case 'spotify': {
        const res = await axios.request({
          method: 'GET',
          url: `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/external/spotify/search`,
          params: {
            type: 'album',
            q: value
          }
        })
        setResults(res.data.albums.items.map((a: any): ExternalAlbum => ({
          provider: 'spotify',
          id: a.id,
          artist: a.artists[0].name,
          name: a.name,
          artwork: a.images[0].url
        })))
        break
      }
      default:
        console.error('search type is invalid')
    }
  }, 500)

  return (
    <div className="scrollable" style={{ display: 'flex', flexDirection: 'column' }}>
      <Alert severity="info" icon={<Science size={24} />}>
        <AlertTitle>BETA MODE</AlertTitle>
        This feature is in <Chip size="small" label="BETA" /> it is not finished and can have bugs, thank you in advance for your understanding
      </Alert>
      <p>Search album using global search engines</p>
      <TextField
        onChange={(e) => search(e.target.value)}
        placeholder="Album or artist name"
      />
      <RadioGroup
        row
        onChange={value => setSearchType(value as SearchType)}
        defaultValue={searchType}
      >
        <FormControlLabel
          control={<Radio value="apple" />}
          label={
            <span>
              <Typography style={{ marginBottom: '-6px' }}>Globally on Apple Music</Typography>
              <Typography variant="caption">The most complete catalog</Typography>
            </span>
          }
        />
        <FormControlLabel
          control={<Radio value="spotify" />}
          label={
            <span>
              <Typography style={{ marginBottom: '-6px' }}>Globally on Spotify</Typography>
              <Typography variant="caption">Unavailable: Spotify closed its API</Typography>
            </span>
          }
        />
        <FormControlLabel
          control={<Radio value="last.fm" />}
          label={
            <span>
              <Typography style={{ marginBottom: '-6px' }}>By album name on Last.fm</Typography>
              <Typography variant="caption">Something edgy</Typography>
            </span>
          }
        />
      </RadioGroup>
      <Grid container spacing={1} style={{ padding: '12px' }}>
        {
          results.map((result, i) => (
            /*
             * Last.fm leaves `id` empty on albums it has no mbid for, so the
             * index is part of the key rather than the id alone.
             */
            <Grid key={`${result.provider}-${result.id}-${i}`} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Card style={{ maxWidth: 300 }}>
                <CardMedia
                  style={{ height: '180px' }}
                  image={result.artwork}
                  title={`Cover of ${result.name} by ${result.artist}`}
                />
                <CardContent>
                  <Typography variant="body1" component="h2" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{result.name}</Typography>
                  <Typography variant="body2" color="textSecondary" component="p" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{result.artist}</Typography>
                  {result.year && (
                    <Typography variant="body2" color="textSecondary" component="p" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {result.year}
                    </Typography>
                  )}
                  {/*
                    * Downloaded already. The partial case says how much of it
                    * we have rather than claiming the album -- and either way
                    * Prepare stays where it is, since re-preparing is how the
                    * rest of a release gets fetched.
                    */}
                  {result.library && (
                    <Chip
                      size="small"
                      color={result.library.complete ? 'success' : 'warning'}
                      label={result.library.complete
                        ? 'In your library'
                        : `Partial (${result.library.completedTracks}/${result.library.trackCount})`}
                      style={{ marginTop: '6px' }}
                    />
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    color="primary"
                    onClick={async () => {
                      navigate(`/music/prepare?artist=${encodeURIComponent(result.artist)}&release=${encodeURIComponent(result.name)}`)
                    }}
                  >
                    Prepare
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        }
      </Grid>
    </div>
  )
}
